import React, { useState } from 'react';
import { Play } from 'lucide-react';
import { apiService } from '../../services';
import { Album, AlbumMedia, AlbumCreate, AlbumStatus } from '../../types';

interface AlbumEditorProps {
  albumId: number;
  initialData?: Album;
  loading: boolean;
  error: string | null;
  mediaItems: AlbumMedia[];
  onUpdate: (data: Partial<AlbumCreate>) => Promise<void>;
  onAddMedia: (files: FileList) => Promise<void>;
  onDeleteMedia: (mediaId: number) => Promise<void>;
}

export const AlbumEditor: React.FC<AlbumEditorProps> = ({
  initialData,
  loading,
  error,
  mediaItems,
  onUpdate,
  onAddMedia,
  onDeleteMedia,
}) => {
  const [formData, setFormData] = useState<AlbumCreate>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    status: (initialData?.status as AlbumStatus) || AlbumStatus.DRAFT,
  });
  const [uploadingFiles, setUploadingFiles] = useState<FileList | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;
    await onUpdate(formData);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles) {
      const invalidFiles: string[] = [];
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const hasExtension = file.name.includes('.') && file.name.split('.').pop()?.length;
        const hasValidType = file.type && file.type.trim() !== '';
        if (!hasExtension || !hasValidType) {
          invalidFiles.push(file.name);
        }
      }
      if (invalidFiles.length > 0) {
        alert(
          `Les fichiers suivants ne sont pas valides:\n${invalidFiles.join('\n')}`
        );
        e.target.value = '';
        setUploadingFiles(null);
        return;
      }
      setUploadingFiles(selectedFiles);
    }
  };

  const handleAddMedia = async () => {
    if (!uploadingFiles || uploadingFiles.length === 0) return;
    await onAddMedia(uploadingFiles);
    setUploadingFiles(null);
    // Reset file input
    const fileInput = document.getElementById('media-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
      <div className='flex justify-between items-center mb-6'>
        <h2 className='text-lg font-semibold text-gray-900'>
          Modifier l&apos;album
        </h2>
      </div>

      {error && (
        <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-md'>
          <p className='text-red-600 text-sm'>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className='space-y-6'>
        {/* Album Info Section */}
        <div className='space-y-4 border-b border-gray-200 pb-6'>
          <h3 className='text-md font-semibold text-gray-900'>Informations de l&apos;album</h3>
          
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              Nom de l&apos;album *
            </label>
            <input
              type='text'
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
              placeholder="Nom de l'album"
              required
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
              rows={3}
              placeholder="Description de l'album"
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              Statut
            </label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.value as AlbumStatus,
                })
              }
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              <option value={AlbumStatus.DRAFT}>Brouillon</option>
              <option value={AlbumStatus.PUBLISHED}>Publié</option>
              <option value={AlbumStatus.PRIVATE}>Privé</option>
            </select>
          </div>

          <div className='flex gap-3 pt-2'>
            <button
              type='submit'
              disabled={loading || !formData.name}
              className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {loading ? 'Sauvegarde...' : 'Sauvegarder les informations'}
            </button>
          </div>
        </div>

        {/* Media Section */}
        <div className='space-y-4'>
          <h3 className='text-md font-semibold text-gray-900'>
            Médias ({mediaItems.length})
          </h3>

          {/* Existing Media Grid */}
          {mediaItems.length > 0 && (
            <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4'>
              {mediaItems.map((media) => (
                <div key={media.id} className='relative group'>
                  <div className='aspect-square rounded-lg overflow-hidden bg-gray-100'>
                    {media.file_type === 'image' ? (
                      <img
                        src={apiService.getAlbumMediaUrl(media.id)}
                        alt={media.title || media.filename}
                        className='w-full h-full object-cover'
                        loading='lazy'
                      />
                    ) : (
                      <div className='w-full h-full flex items-center justify-center bg-gray-900'>
                        <div className='text-white text-center'>
                          <Play className='w-8 h-8 mx-auto mb-1' />
                          <p className='text-xs'>{media.title || 'Vidéo'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    type='button'
                    onClick={() => onDeleteMedia(media.id)}
                    className='absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600'
                    title='Supprimer'
                  >
                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add Media Section */}
          <div className='border-t border-gray-200 pt-4'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Ajouter des médias
            </label>
            <div className='flex gap-3'>
              <input
                id='media-upload'
                type='file'
                multiple
                accept='image/*,video/*'
                onChange={handleFileChange}
                className='flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
              />
              <button
                type='button'
                onClick={handleAddMedia}
                disabled={!uploadingFiles || uploadingFiles.length === 0 || loading}
                className='px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {loading ? 'Ajout...' : 'Ajouter'}
              </button>
            </div>
            {uploadingFiles && uploadingFiles.length > 0 && (
              <p className='mt-2 text-sm text-gray-500'>
                {uploadingFiles.length} fichier(s) sélectionné(s)
              </p>
            )}
            <p className='mt-1 text-xs text-gray-500'>
              Sélectionnez des fichiers d&apos;images ou de vidéos. Max 50MB par fichier.
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};
